import {
    AppState,
    entitledToViewContent,
    Issue,
    Product,
} from '@news-mono/web-common'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAuthActions } from '../../../user-registration'
import { addListener, removeListener } from '../../../__helpers'
import { useProduct } from '../../../__product'
import { calcRem } from '../../../__styling'
import { InternalMastheadLogo } from '../../TheWestHeader'
import { EpaperCollection } from './EpaperCollection'
import { StyledLatestEditionContainer } from './EpaperCollection.styled'
import {
    StyledFaqText,
    StyledIconCross,
    StyledIconCrossSpanWrapper,
    StyledImage,
    StyledLatestEditionHeading,
    StyledModal,
    StyledModalMain,
    StyledOutlinedButton,
    StyledSubscribeButton,
    StyledText,
} from './LatestEdition.styled'

export interface LatestEditionProps {
    latestEdition: Issue
    allEditions: Issue[]
    onEvent: (event: any) => void
}

const newspapersGrouped = require('../assets/newspapers-grouped.jpg')

export const LatestEdition: React.FC<LatestEditionProps> = (props) => {
    const product = useProduct()
    const [showModal, setShowModal] = useState(false)
    const innerModalRef = useRef(null)

    const { onSubscribeClick, onLoginClick } = useAuthActions()
    const { isLoggedIn, subscriptionType } = useSelector(
        (state: AppState) => state.authentication,
    )

    const isEntitled = entitledToViewContent(
        {
            level: 'subscriber',
        },
        useSelector((state: AppState) => state.authentication),
    )

    const handleClickOutsideModal = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.currentTarget !== e.target) return
        if (e.target !== innerModalRef.current) {
            setShowModal(false)
        }
    }

    const handleEscapeKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.type === 'keydown' && e.key === 'Escape') {
            setShowModal(false)
        }
    }

    const handleTabKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
        if (
            e.type === 'keydown' &&
            e.key === 'Tab' &&
            e.target === faqLinkRef.current
        ) {
            e.preventDefault()
            crossIconRef.current?.focus()
        }
    }

    const crossIconRef = useRef<HTMLButtonElement>(null)
    const faqLinkRef = useRef<HTMLAnchorElement>(null)

    useEffect(() => {
        crossIconRef.current?.focus()
    }, [showModal])

    useEffect(() => {
        addListener('keydown', handleEscapeKeyPress)
        addListener('keydown', handleTabKeyPress)
        return () => {
            removeListener('keydown', handleEscapeKeyPress)
            removeListener('keydown', handleTabKeyPress)
        }
    }, [])

    const handleOnCardClick = (link?: string) => {
        if (product === Product.TheWest) {
            // If now logged in, then show the modal
            if (!isLoggedIn) {
                setShowModal(true)
                return
            }

            // If logged in but not entitled, then show the modal
            if (isLoggedIn && !isEntitled) {
                setShowModal(true)
                return
            }
            // If logged in and entitled, then go to the epaper
            window.open(link, '_blank')
        } else {
            window.open(link, '_blank')
        }
    }

    return (
        <>
            <StyledModal
                showModal={showModal}
                onClick={handleClickOutsideModal}
            >
                <StyledModalMain className="modal-main" ref={innerModalRef}>
                    <StyledIconCrossSpanWrapper
                        tabIndex={0}
                        ref={crossIconRef}
                        onClick={() => {
                            setShowModal(false)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setShowModal(false)
                            }
                        }}
                    >
                        <StyledIconCross />
                    </StyledIconCrossSpanWrapper>
                    <div style={{ width: '80%', marginTop: calcRem(40) }}>
                        <InternalMastheadLogo />
                    </div>
                    <StyledText>Read the paper, as it was printed.</StyledText>
                    <StyledImage src={newspapersGrouped} alt=""></StyledImage>
                    <StyledSubscribeButton
                        onClick={() => {
                            setShowModal(false)
                            window.open('/editions/subscribe')
                        }}
                    >
                        Subscribe from{' '}
                        <text style={{ textDecoration: 'underline' }}>
                            $1 per day
                        </text>
                    </StyledSubscribeButton>
                    {!isLoggedIn && (
                        <StyledOutlinedButton
                            onClick={onLoginClick}
                            onFocus={() => console.log('Button Focused')}
                        >
                            Log in
                        </StyledOutlinedButton>
                    )}
                    <StyledFaqText>
                        New to ePapers?{' '}
                        <Link ref={faqLinkRef} to={'/editions/faq'}>
                            Learn more
                        </Link>
                    </StyledFaqText>
                </StyledModalMain>
            </StyledModal>
            <StyledLatestEditionHeading>
                Latest editions
            </StyledLatestEditionHeading>
            <StyledLatestEditionContainer>
                <EpaperCollection
                    openEReaderOnCardClick={true}
                    showMastheadName={false}
                    issues={props.allEditions}
                    onCardClick={(link) => handleOnCardClick(link)}
                    border={true}
                    ReadersAlsoLove={false}
                />
            </StyledLatestEditionContainer>
        </>
    )
}
